import { HttpClient, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Inject, Injectable, InjectionToken, Injector, Optional } from '@angular/core';
import { Observable } from 'rxjs';

import { AccessTokenInterceptor } from './access-token.interceptor';
import { ApiPrefixInterceptor } from './api-prefix.interceptor';
import { AuthorizationInterceptor } from './authorization.interceptor';
import { BasicAuthorizationInterceptor } from './basic-authorization.interceptor';
import { CacheInterceptor } from './cache.interceptor';
import { ErrorHandlerInterceptor } from './error-handler.interceptor';
import { HttpCacheService } from './http-cache.service';
import { LoadingInterceptor } from './loading.interceptor';

// From @angular/common/http/src/interceptor: allows to chain interceptors
class HttpInterceptorHandler implements HttpHandler {
  constructor(
    private next: HttpHandler,
    private interceptor: HttpInterceptor,
  ) {}

  handle(request: HttpRequest<unknown>): Observable<HttpEvent<unknown>> {
    return this.interceptor.intercept(request, this.next);
  }
}

/**
 * Allows to override default dynamic interceptors that can be disabled with the HttpService extension.
 * Except for very specific needs, you should better configure these interceptors directly in the constructor below
 * for better readability.
 *
 * For static interceptors that should always be enabled (like ApiPrefixInterceptor), use the standard
 * HTTP_INTERCEPTORS token.
 */
export const HTTP_DYNAMIC_INTERCEPTORS = new InjectionToken<HttpInterceptor>('HTTP_DYNAMIC_INTERCEPTORS');

/**
 * Extends HttpClient with per request configuration using dynamic interceptors.
 */
@Injectable()
export class HttpService extends HttpClient {
  constructor(
    @Inject(HttpHandler) private httpHandler: HttpHandler,
    @Inject(Injector) private injector: Injector,
    @Optional() @Inject(HTTP_DYNAMIC_INTERCEPTORS) private interceptors: HttpInterceptor[] = [],
  ) {
    super(httpHandler);

    if (!this.interceptors) {
      // Configure default interceptors that can be disabled here
      this.interceptors = [
        this.injector.get(ApiPrefixInterceptor),
        this.injector.get(ErrorHandlerInterceptor),
        this.injector.get(AccessTokenInterceptor),
        this.injector.get(AuthorizationInterceptor),
      ];
    }
  }

  override cache(forceUpdate?: boolean, removeCache?: boolean): HttpClient {
    if (removeCache === true) {
      const httpCacheService = this.injector.get(HttpCacheService);
      httpCacheService.cleanCache();
      return this;
    } else {
      const cacheInterceptor = this.injector.get(CacheInterceptor).configure({ update: forceUpdate });
      return this.addInterceptor(cacheInterceptor);
    }
  }

  override skipErrorHandler(): HttpClient {
    return this.removeInterceptor(ErrorHandlerInterceptor);
  }

  override disableApiPrefix(): HttpClient {
    return this.removeInterceptor(ApiPrefixInterceptor);
  }

  override disableLoading(): HttpClient {
    return this.removeInterceptor(LoadingInterceptor);
  }

  override disableAccessToken(): HttpClient {
    return this.removeInterceptor(AccessTokenInterceptor);
  }

  override disableAuthorizationInterceptor(): HttpClient {
    return this.removeInterceptor(AuthorizationInterceptor);
  }

  override enableBasicAuthorization(): HttpClient {
    const basicAuthorizationInterceptor = this.injector.get(BasicAuthorizationInterceptor);
    return this.addInterceptor(basicAuthorizationInterceptor);
  }

  // Override the original method to wire interceptors when triggering the request.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  override request(method?: any, url?: any, options?: any): any {
    const handler = this.interceptors.reduceRight(
      (next, interceptor) => new HttpInterceptorHandler(next, interceptor),
      this.httpHandler,
    );
    return new HttpClient(handler).request(method, url, options);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private removeInterceptor(interceptorType: new (...args: any[]) => HttpInterceptor): HttpService {
    return new HttpService(
      this.httpHandler,
      this.injector,
      this.interceptors.filter((i) => !(i instanceof interceptorType)),
    );
  }

  private addInterceptor(interceptor: HttpInterceptor): HttpService {
    return new HttpService(this.httpHandler, this.injector, this.interceptors.concat([interceptor]));
  }
}
